import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

export interface TokenQuota {
  monthlyAllocation: number;
  used: number;
  remaining: number;
  usagePercentage: number;
}

export interface User {
  id: string;
  publicId?: string;
  email: string;
  name?: string;
  nickname?: string;
  profileImage?: string;
  bio?: string;
  interests?: string[];
  country?: string;
  region?: string;
  language?: string;
  secondaryLanguage?: string | null;
  linkedProviders?: string[];
  tokenQuota?: TokenQuota;
  createdAt?: string;
}

interface AuthState {
  // Authentication state
  user: User | null;
  isAuthenticated: boolean;
  accessToken: string | null;
  refreshToken: string | null;
  
  // Loading states
  isLoading: boolean;
  isInitialized: boolean;
  
  // Actions
  login: (user: User, accessToken: string, refreshToken: string) => void;
  logout: () => void;
  updateUser: (user: Partial<User>) => void;
  setTokens: (accessToken: string, refreshToken: string) => void;
  updateTokens: (accessToken: string, refreshToken: string) => void;
  clearTokens: () => void;
  setLoading: (loading: boolean) => void;
  initialize: () => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        user: null,
        isAuthenticated: false,
        accessToken: null,
        refreshToken: null,
        isLoading: false,
        isInitialized: false,
        
        // Login action
        login: (user, accessToken, refreshToken) => {
          set({
            user,
            isAuthenticated: true,
            accessToken,
            refreshToken,
            isLoading: false,
          });
        },
        
        // Logout action
        logout: () => {
          set({
            user: null,
            isAuthenticated: false,
            accessToken: null,
            refreshToken: null,
          });
          // Clear any user-specific data from graph store if needed
          // This will be implemented when integrating with existing stores
        },
        
        // Update user information
        updateUser: (updates) => {
          const currentUser = get().user;
          if (currentUser) {
            set({
              user: { ...currentUser, ...updates }
            });
          }
        },
        
        // Set tokens (also used by interceptor)
        setTokens: (accessToken, refreshToken) => {
          set({ accessToken, refreshToken });
        },
        
        // Update tokens (alias for setTokens for compatibility)
        updateTokens: (accessToken, refreshToken) => {
          set({ accessToken, refreshToken });
        },
        
        // Clear tokens
        clearTokens: () => {
          set({ accessToken: null, refreshToken: null });
        },
        
        // Set loading state
        setLoading: (loading) => {
          set({ isLoading: loading });
        },
        
        // Initialize auth state (check token validity, etc.)
        initialize: async () => {
          const { accessToken, refreshToken, isAuthenticated } = get();

          if (!accessToken || !refreshToken) {
            set({ isInitialized: true, isAuthenticated: false });
            return;
          }

          // If authenticated, fetch current user data including token quota
          if (isAuthenticated) {
            try {
              const { AuthService } = await import('@/services/auth.service');
              const userData = await AuthService.getCurrentUser();
              set({
                user: userData,
                isInitialized: true
              });
            } catch (error) {
              console.error('Failed to fetch user data:', error);
              set({
                user: null,
                isAuthenticated: false,
                accessToken: null,
                refreshToken: null,
                isInitialized: true,
              });
            }
          } else {
            set({ isInitialized: true });
          }
        },
      }),
      {
        name: 'auth-storage',
        partialize: (state) => ({
          user: state.user,
          accessToken: state.accessToken,
          refreshToken: state.refreshToken,
          isAuthenticated: state.isAuthenticated,
        }),
      }
    ),
    {
      name: 'auth-store',
    }
  )
);

// Helper hook to check if user has specific provider linked
export const useHasProvider = (provider: string) => {
  const user = useAuthStore((state) => state.user);
  return user?.linkedProviders?.includes(provider) ?? false;
};

// Helper hook for auth status
export const useIsAuthenticated = () => {
  return useAuthStore((state) => state.isAuthenticated);
};
